import React from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { heroPortrait } from "../mock";
import { useSiteData } from "../context/SiteDataContext";
import { uniqueLabels } from "../lib/contentCollections";
import { getSection, imageStyle, renderMultilineText, sectionStyle } from "../lib/sectionStyles";
import SafeImage from "../components/SafeImage";

const ActionLink = ({ to, children, className }) => {
  if (to?.startsWith("http") || to?.startsWith("mailto:")) {
    return (
      <a href={to} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to || "/journalist-portfolio"} className={className}>
      {children}
    </Link>
  );
};

const Hero = () => {
  const { writer, siteSections } = useSiteData();
  const hero = getSection(siteSections, "home", "home.hero", {
    title: "MAI EL\nMOKADEM.",
    subtitle: writer.bio,
    eyebrow: "The Writing Portfolio - Front Cover",
    label: "The Byline - Issue 26",
    caption: "written, not styled.",
    imageUrl: heroPortrait,
    imageAlt: "Editorial portrait",
    imagePosition: "center",
    imageFit: "cover",
    ctaLabel: "CONTACT ME FOR COMMISSIONS, PITCHES, HIRE ME",
    ctaUrl: "/journalist-portfolio",
    content: {
      metaLeft: `VOL. 01 - ${writer.issue} - ${writer.year}`,
      metaCenter: "For the byline. For the beat.",
      metaRight: writer.location,
      issueStamp: "EDITORIAL ISSUE",
      tags: writer.taglineLines,
      handwrittenCta: "start here ->",
    },
  });
  const sidePanel = getSection(siteSections, "home", "home.hero-side-panel", {
    label: "IN THIS ISSUE",
    body: '"the region is the room."',
    content: {
      items: [
        "P.04 - The Habibification of House Music",
        "P.12 - Sarah Sahabati, in her own words",
        "P.18 - In the Arab World, Love Walks on Tiptoe",
        "P.24 - Tara Babylon, born of an imaginary aura",
      ],
    },
  });

  if (!hero) return null;

  const titleLines = String(hero.title || writer.name).split(/\n+/).filter(Boolean);
  const firstTitle = titleLines.length > 1 ? titleLines.slice(0, -1).join(" ") : "MAI EL";
  const lastTitle = titleLines.length > 1 ? titleLines[titleLines.length - 1] : titleLines[0] || "MOKADEM.";
  const lastTitleText = lastTitle.replace(/\.$/, "");
  const rawTags = Array.isArray(hero.content?.tags) && hero.content.tags.length ? hero.content.tags : writer.taglineLines;
  const tags = uniqueLabels(rawTags);
  const sideItems = Array.isArray(sidePanel?.content?.items) ? sidePanel.content.items : [];

  return (
    <section className="relative paper-grain overflow-hidden" style={sectionStyle(hero)}>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-10 pb-6">
        <div className="hidden md:flex items-center gap-2 mb-3">
          <span className="h-2 w-10 bg-[var(--pink)]" />
          <span className="h-2 w-2 bg-[var(--red)]" />
          <span className="h-2 w-24 bg-[var(--pink)]" />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--ink-soft)] ml-1">
            {hero.eyebrow}
          </span>
        </div>

        <div className="flex items-end justify-between gap-4 border-b-2 border-[var(--ink)] pb-3">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--ink-soft)]">
            {hero.content?.metaLeft || `VOL. 01 - ${writer.issue} - ${writer.year}`}
          </div>
          <div className="hidden sm:block font-editorial italic text-base text-[var(--red-deep)]">
            {hero.content?.metaCenter || "For the byline. For the beat."}
          </div>
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase">
            {hero.content?.metaRight || writer.location}
          </div>
        </div>

        <div className="relative pt-8 pb-2">
          <div className="absolute -top-1 right-0 stamp">{hero.content?.issueStamp || "EDITORIAL ISSUE"}</div>
          <h1 className="font-masthead leading-[0.82] tracking-tight text-[18vw] sm:text-[16vw] lg:text-[14.5vw] xl:text-[230px]">
            <span className="block">{firstTitle}</span>
            <span className="block text-[var(--red)] relative">
              {lastTitleText}
              <span className="text-[var(--ink)]">.</span>
              <span
                className="absolute left-0 right-[8%] -bottom-1 h-3 bg-[var(--pink)] -z-0 opacity-90"
                aria-hidden="true"
                style={{ zIndex: -1 }}
              />
            </span>
          </h1>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 mt-6 md:mt-10 items-start">
          <div className="col-span-12 md:col-span-5 order-2 md:order-1">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--ink-soft)] mb-3">
              {hero.label}
            </p>
            <h2 className="font-editorial text-3xl md:text-4xl leading-[1.1]">
              {renderMultilineText(hero.subtitle)}
            </h2>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-tags">
              {tags.map((tag) => (
                <span
                  key={tag}
                  data-hero-tag={tag.toLocaleLowerCase()}
                  className="font-masthead text-sm tracking-[0.22em] px-3 py-1 border-2 border-[var(--ink)] bg-[var(--pink-soft)]"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-4">
              <ActionLink
                to={hero.ctaUrl || "/journalist-portfolio"}
                className="group inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] px-5 py-3 font-masthead tracking-[0.22em] text-sm hover:bg-[var(--red)] transition-colors"
              >
                {hero.ctaLabel || "READ THE ISSUE"}
                <ArrowDownRight
                  size={16}
                  className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform"
                />
              </ActionLink>
              <span className="font-hand text-2xl text-[var(--red)] -rotate-6">
                {hero.content?.handwrittenCta || "start here ->"}
              </span>
            </div>
          </div>

          <div className="col-span-12 md:col-span-4 order-1 md:order-2 relative">
            <div className="relative">
              <span className="tape -top-3 left-6 -rotate-6" />
              <span className="tape -top-3 right-8 rotate-6" />
              <div className="aspect-[4/5] w-full overflow-hidden border-4 border-[var(--ink)] bg-[var(--ink)] relative">
                <SafeImage
                  src={hero.imageUrl || heroPortrait}
                  fallbackSrc={heroPortrait}
                  alt={hero.imageAlt || "Editorial portrait"}
                  fetchPriority="high"
                  width="940"
                  height="1175"
                  className="w-full h-full object-cover duotone-red"
                  style={imageStyle(hero)}
                />
                <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-[var(--red)]/95 text-[var(--pink-soft)] flex items-center justify-between">
                  <span className="font-masthead tracking-[0.25em] text-xs">PORTRAIT No. 01</span>
                  <span className="font-mono text-[10px]">PHOTO: ARCHIVAL</span>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-4 rotate-3 bg-[var(--paper)] border-2 border-[var(--ink)] px-3 py-2 shadow-[3px_3px_0_var(--ink)]">
                <span className="font-hand text-xl text-[var(--red-deep)]">
                  {hero.caption || "written, not styled."}
                </span>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-3 order-3 md:order-3 mt-2 md:mt-0">
            {sidePanel ? (
              <div className="border-2 border-[var(--ink)] bg-[var(--pink-soft)] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-[var(--red)]" />
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
                    {sidePanel.label || "In this issue"}
                  </span>
                </div>
                <ul className="space-y-3 font-editorial text-base leading-snug">
                  {sideItems.map((item) => {
                    const [prefix, ...rest] = String(item).split(" - ");
                    return (
                      <li key={item}>
                        <span className="font-masthead text-xs tracking-[0.2em] text-[var(--red)]">
                          {rest.length ? `${prefix} - ` : ""}
                        </span>
                        {rest.join(" - ") || item}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <p className="mt-4 font-hand text-xl text-[var(--ink-soft)] -rotate-1">
              {sidePanel?.body || '"the region is the room."'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
